<template>
  <div>
    <!-- Hero Section -->
    <Block1Col>
      <MARSTitle>
        <span class="whitespace-nowrap">{{ $t('configurator.hero') }}</span>
      </MARSTitle>
      <p class="text-xl mt-4">{{ $t('configurator.hero_subtitle') }}</p>
    </Block1Col>

    <!-- Visual Sections -->
    <Block2Cols class="mt-16">
      <template #col1>
        <MARSTitle>{{ $t('configurator.whatIs_title') }}</MARSTitle>
        <p v-html="$t('configurator.whatIs_text')"></p>
      </template>
      <template #col2>
        <!-- <img loading="lazy" src="~/assets/img/configurator/shoes.webp" alt="Configurator Example - Shoes" class="rounded-lg shadow-lg"> -->
        <video
          src="~/assets/img/configurator/configurator-demo.mp4"
          autoplay
          muted
          loop
          playsinline
          class="rounded-lg shadow-lg"
          style="width: 100%; height: auto;"
        ></video>
      </template>
    </Block2Cols>

    <!-- 
    TODO: restore when available
    <Block2Cols class="mt-16" reverse>
      <template #col1>
        <MARSTitle>{{ $t('configurator.materials_library_title') }}</MARSTitle>
        <p v-html="$t('configurator.materials_library_text')"></p>
      </template>
      <template #col2>
        <img loading="lazy" src="~/assets/img/pricing/MaterialsLibrary-Roadmap.webp" alt="Materials Library" class="rounded-lg shadow-lg">
      </template>
    </Block2Cols> -->

    <Block2Cols class="mt-16">
      <template #col1>
        <MARSTitle>{{ $t('configurator.neutral_base_materials_title') }}</MARSTitle>
        <p v-html="$t('configurator.neutral_base_materials_text')"></p>
      </template>
      <template #col2>
        <img loading="lazy" src="~/assets/img/configurator/neutral-base-materials.webp" alt="Neutral Base Materials" class="rounded-lg shadow-lg">
      </template>
    </Block2Cols>

    <Block2Cols class="mt-16" reverse>
      <template #col1>
        <MARSTitle>{{ $t('configurator.tint_title') }}</MARSTitle>
        <p v-html="$t('configurator.tint_text')"></p>
      </template>
      <template #col2>
        <img loading="lazy" src="~/assets/img/configurator/tints.webp" alt="Tints can be applied to neutral base materials to easily customize the color" class="rounded-lg shadow-lg">
      </template>
    </Block2Cols>

    <Block2Cols class="mt-16">
      <template #col1>
        <MARSTitle>{{ $t('configurator.geometry_variants_title') }}</MARSTitle>
        <p v-html="$t('configurator.geometry_variants_description')"></p>
      </template>
      <template #col2>
        <video
          src="~/assets/img/configurator/geometry-variants.mp4"
          autoplay
          muted
          loop
          playsinline
          class="rounded-lg shadow-lg"
          style="width: 100%; height: auto;"
        ></video>
      </template>
    </Block2Cols>

    <Block2Cols class="mt-16" reverse>
      <template #col1>
        <MARSTitle>{{ $t('configurator.embeddable_title') }}</MARSTitle>
        <p v-html="$t('configurator.embeddable_description')"></p>
      </template>
      <template #col2>
        <video
          src="~/assets/img/configurator/embed-the-configurator.mp4"
          autoplay
          muted
          loop
          playsinline
          class="rounded-lg shadow-lg"
          style="width: 100%; height: auto;"
        ></video>
      </template>
    </Block2Cols>

    <Block1Col class="mt-16
     py-12">
        <MARSTitle>{{ $t('configurator.theme_title') }}</MARSTitle>
        <p v-html="$t('configurator.theme_description')"></p>
        <!-- Aspect ratio must be exact, set also max-width <= image size -->
        <div class="mt-16 mx-auto w-full"  style="aspect-ratio: 1.766; max-width: 982px;">
          <ImageCompare
            :leftImage="compareLightImg"
            :rightImage="compareDarkImg"
            leftAlt="Light Mode"
            rightAlt="Dark Mode"
            style="width: 100%; height: 100%; object-fit: contain;"
          />
        </div>
    </Block1Col>

    <Block2Cols class="mt-16" reverse>
      <template #col1>
        <MARSTitle>{{ $t('configurator.sku_title') }}</MARSTitle>
        <p v-html="$t('configurator.sku_description')"></p>
      </template>
      <template #col2>
        <video
          src="~/assets/img/configurator/automatic-SKU-generation.mp4"
          autoplay
          muted
          loop
          playsinline
          class="rounded-lg shadow-lg"
          style="width: 100%; height: auto;"
        ></video>
      </template>
    </Block2Cols>

    <!-- Live Demo Section -->
    <Block1Col class="mt-16">
      <MARSTitle>{{ $t('configurator.live_demo_title') }}</MARSTitle>
      <p class="mb-8" v-html="$t('configurator.live_demo_text')"></p>
      <div class="w-full h-iframeContainer md:h-iframeContainerMd lg:mt-0 viewer3d" style="min-height: 700px; border: 1px solid lightgray; background-color: white; display: flex; align-items: center; justify-content: center;">
      <iframe
        :src="`https://viewer.dev.myarstudio.cloud/configurator_website/viewer.html?b=f99570cc-2a34-4aba-b9cb-51f7b7a07226&p=78e0d91f-1273-43cb-9771-866af3898972&c=09abae61-5e25-5933-bf9f-d5d715be2c6b&lang=${locale}`"
        title="Interactive Configurator Demo"
        style="width:100%; height:100%; border:0;"
        allowfullscreen
        loading="lazy"
      ></iframe>
      </div>
    </Block1Col>

    <!-- Features Section -->
    <Block1Col class="mt-16">
      <MARSTitle>{{ $t('configurator.features_title') }}</MARSTitle>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
        <div v-for="(feature, key) in featureEntries" :key="key" class="flex items-center">
          <div class="flex items-center">
            <CheckMark class="w-6 h-6 mr-3 fill-highlight flex-shrink-0" />
            <div><p>{{ $t(`configurator.features.${key}`) }}</p></div>
          </div>
        </div>
      </div>
    </Block1Col>

    <Block1Col class="mt-16">
      <MARSTitle>{{ $t('configurator.why_it_matters_title') }}</MARSTitle>
    </Block1Col>
    <Block2Cols compact>
      <template #col1>
        <h3>{{ $t('configurator.why_it_matters_subtitle_1') }}</h3>
        <p v-html="$t('configurator.why_it_matters_description_1')"></p>
      </template>
      <template #col2>
        <h3>{{ $t('configurator.why_it_matters_subtitle_2') }}</h3>
        <p v-html="$t('configurator.why_it_matters_description_2')"></p>
      </template>
    </Block2Cols>

    <!-- CTA -->
    <Block2Cols class="mt-16">
      <template #col1>
        <MARSTitle>{{ $t('configurator.pilot_title') }}</MARSTitle>
        <p v-html="$t('configurator.pilot_description')"></p>
      </template>
      <template #col2>
        <p v-html="$t('configurator.pilot_description_2')"></p>
        <p v-html="$t('configurator.pilot_description_3')"></p>
        <ActionButton :href="$t('links.configurator_pilot_form')" :value="$t('configurator.cta')" :bg="Colors.orange" :fg="Colors.white" class="text-xl px-8 py-4" />
      </template>
    </Block2Cols>

    <!-- Use Cases Section -->
    <!-- <BlockBand class="mt-16" bandColor="#F2F2F2">
      <Block1Col>
        <MARSTitle>{{ $t('configurator.use_cases_title') }}</MARSTitle>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
          <div v-for="(useCase, key) in use_casesEntries" :key="key" class="bg-white p-6 rounded-lg shadow-md">
            <p class="font-semibold">{{ $t(`configurator.use_cases.${key}`) }}</p>
          </div>
        </div>
      </Block1Col>
    </BlockBand> -->
  </div>
</template>

<script lang="ts" setup>
import { Colors } from '~/utils/generic'
import ImageCompare from '~/components/ImageCompare.vue'
import compareLightImg from '~/assets/img/configurator/compare-light-mode.webp'
import compareDarkImg from '~/assets/img/configurator/compare-dark-mode.webp'
const { locale, t, tm, rt, te } = useI18n();

const featureEntries = computed(() => tm('configurator.features') as Record<string, string>);
const use_casesEntries = computed(() => tm('configurator.use_cases') as Record<string, string>);
console.log("Locale:", locale)
</script>

<style scoped>
/* Add any specific styles here if needed */
</style>
